/**
 * ame_bundle-builder.js
 *
 * Client-side behavior for the B2G1 Necklace Bundle-Builder page (/pages/build-your-free-necklace-bundle).
 *
 * Responsibilities:
 *   - Category switching: read ?category= URL param, toggle product card visibility,
 *     update URL silently via history.replaceState so refresh preserves selection
 *     without triggering navigation.
 *   - Cart actions: Claim button POSTs to /cart/add.js silently (no cart:add event
 *     dispatched = no drawer auto-open). Qty selector +/- POSTs to /cart/change.js.
 *     On page load, /cart.js sync sets each card's initial display state.
 *   - Quick View modal: clicking the card image or title opens a <dialog> with the
 *     variant's data (pulled from .ame-bundle-card__quick-view-data JSON embedded
 *     per card). Carousel for multiple images, Claim/qty inside modal syncs with
 *     the main card display.
 *   - Live B2G1 savings math in the sticky bar: after every cart change, refetch
 *     the cart and update the sticky bar info text + checkout button label.
 *     Three states based on necklace count: empty (0), partial (1-2), active (3+).
 *
 * Phase 2 polish remaining:
 *   - iOS Safari sticky bar address-bar jitter polish
 *   - Touch-swipe support for modal carousel
 */

(() => {
  'use strict';

  const DEFAULT_CATEGORY = 'premium';
  const QUICK_VIEW_ID = 'ame-bundle-quick-view';

  /* SVG icon strings used in JS-generated HTML.  Matches the Liquid snippet
     'ame_bundle-icon' for consistency.  Color is controlled via parent
     `currentColor` so CSS handles the visual styling. */
  const ICON_PLUS =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14"/><path d="M5 12h14"/></svg>';
  const ICON_MINUS =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/></svg>';
  const ICON_STAR_FILLED =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>';
  const ICON_STAR_OUTLINE =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>';

  /* ============================================================================
   * Category switching
   * ============================================================================ */

  const getCategoryFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return (params.get('category') || DEFAULT_CATEGORY).toLowerCase();
  };

  const updateCategoryCardSelection = (category) => {
    const cards = document.querySelectorAll('.ame-bundle-builder__category-card');
    cards.forEach((card) => {
      const isSelected = card.dataset.category === category;
      card.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
  };

  /**
   * Categories that have at least one (in-stock) card rendered in the grid.
   * OOS products are hidden server-side, so a category with zero cards is an
   * empty range. Used to drive the empty-range message + its CTA target.
   */
  const categoriesWithCards = () => {
    const set = new Set();
    document.querySelectorAll('.ame-bundle-card[data-category]').forEach((card) => {
      set.add(card.dataset.category);
    });
    return set;
  };

  /**
   * Sort the grid cards by their 30d revenue (data-revenue) descending so the
   * best sellers lead each range - the Option A Flow sort William asked us to
   * make sure is actually applied (5/30; previously the grid rendered in the
   * collection's natural order). Reorders DOM nodes in place; category
   * visibility is toggled separately, so cross-category interleaving is
   * invisible. Stable for equal-revenue cards (preserves server order).
   */
  const sortGridByRevenue = () => {
    const grid = document.querySelector('.ame-bundle-builder__grid');
    if (!grid) return;
    const cards = [...grid.querySelectorAll('.ame-bundle-card[data-category]')];
    if (cards.length < 2) return;
    cards
      .map((el, i) => ({ el, i, rev: parseFloat(el.dataset.revenue) || 0 }))
      .sort((a, b) => (b.rev - a.rev) || (a.i - b.i))
      .forEach(({ el }) => grid.appendChild(el));
  };

  const updateProductVisibility = (category) => {
    const grid = document.querySelector('.ame-bundle-builder__grid');
    if (grid) {
      grid.dataset.activeCategory = category;
    }

    let visibleCount = 0;
    const cards = document.querySelectorAll('.ame-bundle-card[data-category]');
    cards.forEach((card) => {
      if (card.dataset.category === category) {
        card.classList.remove('ame-bundle-card--inactive');
        visibleCount += 1;
      } else {
        card.classList.add('ame-bundle-card--inactive');
      }
    });

    // QA (5/30): empty-range message. If the selected range has no in-stock
    // products, hide the (empty) grid, show the message, and point its button
    // at the first range that does have stock.
    const emptyEl = document.querySelector('.ame-bundle-builder__range-empty');
    if (emptyEl) {
      if (visibleCount === 0) {
        if (grid) grid.hidden = true;
        emptyEl.hidden = false;
        const inStock = [...categoriesWithCards()].filter((c) => c !== category);
        const btn = emptyEl.querySelector('[data-action="goto-in-stock"]');
        if (btn) {
          if (inStock.length) {
            btn.dataset.target = inStock[0];
            btn.hidden = false;
          } else {
            // Nothing in stock anywhere - hide the button, leave the message.
            btn.hidden = true;
          }
        }
      } else {
        if (grid) grid.hidden = false;
        emptyEl.hidden = true;
      }
    }
  };

  const syncUrl = (category) => {
    const url = new URL(window.location.href);
    url.searchParams.set('category', category);
    window.history.replaceState({}, '', url);
  };

  const setCategory = (category) => {
    updateCategoryCardSelection(category);
    updateProductVisibility(category);
    syncUrl(category);
  };

  const wireCategoryClicks = () => {
    const cards = document.querySelectorAll('.ame-bundle-builder__category-card');
    cards.forEach((card) => {
      card.addEventListener('click', (event) => {
        event.preventDefault();
        const category = card.dataset.category;
        if (category) {
          setCategory(category);
        }
      });
    });

    // Empty-range CTA: jump to the first in-stock range (target set by
    // updateProductVisibility).
    const emptyBtn = document.querySelector('[data-action="goto-in-stock"]');
    if (emptyBtn) {
      emptyBtn.addEventListener('click', () => {
        const target = emptyBtn.dataset.target;
        if (target) setCategory(target);
      });
    }
  };

  /* ============================================================================
   * Cart interaction (silent - no drawer pop)
   * ============================================================================ */

  const postJson = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(`${url} failed: ${response.status}`);
    }
    return response.json();
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) {
      throw new Error(`/cart.js failed: ${response.status}`);
    }
    return response.json();
  };

  const getCurrentQty = (variantId) => {
    const card = document.querySelector(`.ame-bundle-card[data-variant-id="${variantId}"]`);
    if (!card) return 0;
    const qtyEl = card.querySelector('.ame-bundle-card__qty-value');
    return parseInt(qtyEl?.dataset.qty || '0', 10);
  };

  /**
   * Render a product card in either the Claim state (qty=0) or qty selector
   * state (qty>0). Also updates the modal's action area when the modal is
   * currently showing this same variant.
   */
  const renderCardForVariant = (variantId, quantity) => {
    const card = document.querySelector(`.ame-bundle-card[data-variant-id="${variantId}"]`);
    if (card) {
      const claimBtn = card.querySelector('.ame-bundle-card__claim');
      const qtyEl = card.querySelector('.ame-bundle-card__qty');
      const qtyValue = card.querySelector('.ame-bundle-card__qty-value');
      if (claimBtn && qtyEl && qtyValue) {
        if (quantity > 0) {
          claimBtn.hidden = true;
          qtyEl.hidden = false;
          qtyValue.textContent = String(quantity);
          qtyValue.dataset.qty = String(quantity);
        } else {
          claimBtn.hidden = false;
          qtyEl.hidden = true;
          qtyValue.textContent = '0';
          qtyValue.dataset.qty = '0';
        }
      }
    }

    // If the modal is currently showing this variant, refresh its action area
    const modal = document.getElementById(QUICK_VIEW_ID);
    if (modal && modal.dataset.variantId === String(variantId)) {
      renderModalAction();
    }
  };

  /**
   * QA1: bump the quantity number when +/- changes it, for tactile feedback.
   * Re-triggers the CSS animation by removing the class, forcing a reflow, then
   * re-adding it. Bumps both the card and (if open) the modal qty value.
   */
  const bumpQtyValue = (variantId) => {
    const els = [
      document.querySelector(`.ame-bundle-card[data-variant-id="${variantId}"] .ame-bundle-card__qty-value`),
      document.querySelector(`#${QUICK_VIEW_ID}[open] .ame-bundle-quick-view__qty-value`),
    ];
    els.forEach((el) => {
      if (!el) return;
      el.classList.remove('ame-bundle--qty-bump');
      void el.offsetWidth;
      el.classList.add('ame-bundle--qty-bump');
    });
  };

  /* ============================================================================
   * B2G1 savings math + sticky bar live updates
   * ============================================================================ */

  /**
   * Product IDs that have a card rendered on this page (i.e. necklaces in
   * "Jewelry - " prefixed collections). Collected once on init. Used to
   * filter cart items so we only count necklace items in the B2G1 math -
   * if the customer has urns or other products in their cart, those don't
   * count toward the necklace bundle discount.
   */
  const NECKLACE_PRODUCT_IDS = new Set();

  const collectNecklaceProductIds = () => {
    document.querySelectorAll('.ame-bundle-card[data-product-id]').forEach((card) => {
      const id = parseInt(card.dataset.productId, 10);
      if (Number.isFinite(id)) NECKLACE_PRODUCT_IDS.add(id);
    });
  };

  /**
   * Calculate B2G1 savings from the cart. Algorithm:
   *   1. Filter cart items to necklaces only (using product IDs collected from rendered cards)
   *   2. Flatten quantities into a list of individual unit prices (in cents)
   *   3. Sort descending so cheapest unit is at the end of the array
   *   4. Every 3rd item starting from index 2 (positions 3, 6, 9...) is the "free" one
   *      in its triple - sum those for total savings
   *
   * Returns { count, savingsCents, needMore }.
   *   - count: total necklaces in cart
   *   - savingsCents: total saved in cents
   *   - needMore: how many more necklaces needed to start saving (0 if already saving)
   */
  const calculateSavings = (cart) => {
    const items = (cart.items || []).filter((item) =>
      NECKLACE_PRODUCT_IDS.has(item.product_id)
    );

    const unitPrices = [];
    items.forEach((item) => {
      for (let i = 0; i < item.quantity; i++) {
        unitPrices.push(item.price);
      }
    });
    // Standard Shopify "cheapest free" B2G1 logic: sort ASCENDING and take the
    // first floor(qty/3) items as the free ones (the absolute cheapest in the
    // cart, regardless of price tier). Confirmed by William 5/27 - matches the
    // default Shopify discount behaviour.
    unitPrices.sort((a, b) => a - b);
    const freeCount = Math.floor(unitPrices.length / 3);

    let savingsCents = 0;
    for (let i = 0; i < freeCount; i++) {
      savingsCents += unitPrices[i];
    }

    return {
      count: unitPrices.length,
      savingsCents,
      needMore: Math.max(0, 3 - unitPrices.length),
    };
  };

  /**
   * Compound price HTML for the savings amount - matches the card price pattern
   * but in brand red (#ad0929) instead of brand blue.
   */
  const renderSavingsPriceHTML = (cents) => {
    const dollars = Math.floor(cents / 100);
    const decimal = (cents % 100).toString().padStart(2, '0');
    return (
      `<span class="ame-bundle-builder__sticky-bar-savings-sub">$</span>` +
      `<span class="ame-bundle-builder__sticky-bar-savings-int">${dollars}</span>` +
      `<span class="ame-bundle-builder__sticky-bar-savings-sub">.${decimal}</span>`
    );
  };

  /**
   * Update the sticky bar info text + checkout button label based on cart state.
   * Three states:
   *   - 0 necklaces:  "Add 3 Necklaces To Start Saving"   |   "Checkout"
   *   - 1-2 necklaces: "Add N More Necklace(s) To Start Saving" |   "Checkout"
   *   - 3+ necklaces:  "You're Saving $X.XX" (red price)  |   "Checkout (N)"
   */
  const updateStickyBar = (cart) => {
    const { count, savingsCents, needMore } = calculateSavings(cart);
    const info = document.querySelector('.ame-bundle-builder__sticky-bar-info');
    const checkoutLabel = document.querySelector('.ame-bundle-builder__sticky-bar-checkout-label');
    const checkout = document.querySelector('.ame-bundle-builder__sticky-bar-checkout');

    if (!info) return;

    // QA2: the checkout button only appears once at least one necklace is in
    // the cart (per Figma). Hidden at count 0 when there's nothing to check out.
    // QA1: once a bundle qualifies (3+), the button gets a periodic attention
    // pulse (CSS animation) to nudge the shopper to check out.
    if (checkout) {
      // visibility:hidden (via class) not the hidden attribute, so the button
      // keeps its space and the sticky bar height stays constant at count 0.
      checkout.classList.toggle('ame-bundle-builder__sticky-bar-checkout--hidden', count === 0);
      checkout.classList.toggle('ame-bundle-builder__sticky-bar-checkout--pulse', count >= 3);
    }

    if (count >= 3) {
      // Active state per Figma node 2842:8534: text + single hairline leader + price.
      // No flanking dashes - the leader sits between the two text elements.
      info.innerHTML =
        '<p class="ame-bundle-builder__sticky-bar-info-text">You&rsquo;re Saving</p>' +
        '<span class="ame-bundle-builder__sticky-bar-dash" aria-hidden="true"></span>' +
        `<p class="ame-bundle-builder__sticky-bar-savings">${renderSavingsPriceHTML(savingsCents)}</p>`;
      if (checkoutLabel) checkoutLabel.textContent = `Checkout (${count})`;
    } else if (count > 0) {
      const word = needMore === 1 ? 'Necklace' : 'Necklaces';
      info.innerHTML =
        '<span class="ame-bundle-builder__sticky-bar-dash" aria-hidden="true"></span>' +
        `<p class="ame-bundle-builder__sticky-bar-info-text">Add ${needMore} More ${word} To Start Saving</p>` +
        '<span class="ame-bundle-builder__sticky-bar-dash" aria-hidden="true"></span>';
      if (checkoutLabel) checkoutLabel.textContent = 'Checkout';
    } else {
      info.innerHTML =
        '<span class="ame-bundle-builder__sticky-bar-dash" aria-hidden="true"></span>' +
        '<p class="ame-bundle-builder__sticky-bar-info-text">Add 3 Necklaces To Start Saving</p>' +
        '<span class="ame-bundle-builder__sticky-bar-dash" aria-hidden="true"></span>';
      if (checkoutLabel) checkoutLabel.textContent = 'Checkout';
    }
  };

  /* ============================================================================
   * Cart sync orchestration
   * ============================================================================ */

  /**
   * Local cache of the cart state. Updated optimistically on user actions and
   * reconciled with server after each API call completes. Lets the sticky bar
   * recalculate B2G1 savings instantly without waiting on /cart.js round-trips.
   */
  let cachedCart = { items: [] };

  /**
   * Read price + product id from the rendered card for a variant - used when
   * adding a NEW item to the local cart cache (the cart doesn't have it yet
   * but the card does via the data-* attributes set in Liquid).
   */
  const getCardMeta = (variantId) => {
    const card = document.querySelector(`.ame-bundle-card[data-variant-id="${variantId}"]`);
    if (!card) return null;
    return {
      priceCents: parseInt(card.dataset.priceCents || '0', 10),
      productId: parseInt(card.dataset.productId || '0', 10),
    };
  };

  /**
   * Apply a cart mutation to the LOCAL cached cart without hitting the API.
   * Used for instant optimistic UI updates. The real API call follows in the
   * background and we reconcile after.
   */
  const applyLocalCartUpdate = (variantId, newQty) => {
    if (!cachedCart.items) cachedCart.items = [];
    const variantIdInt = parseInt(variantId, 10);
    const idx = cachedCart.items.findIndex((i) => i.variant_id === variantIdInt);

    if (idx >= 0) {
      if (newQty === 0) {
        cachedCart.items.splice(idx, 1);
      } else {
        cachedCart.items[idx] = { ...cachedCart.items[idx], quantity: newQty };
      }
    } else if (newQty > 0) {
      const meta = getCardMeta(variantId);
      if (meta) {
        cachedCart.items.push({
          variant_id: variantIdInt,
          product_id: meta.productId,
          price: meta.priceCents,
          quantity: newQty,
        });
      }
    }
  };

  const syncCartState = async () => {
    try {
      const cart = await fetchCart();
      cachedCart = cart;
      const variantQuantities = new Map();
      (cart.items || []).forEach((item) => {
        variantQuantities.set(String(item.variant_id), item.quantity);
      });

      const cards = document.querySelectorAll('.ame-bundle-card[data-variant-id]');
      cards.forEach((card) => {
        const variantId = card.dataset.variantId;
        const qty = variantQuantities.get(variantId) || 0;
        renderCardForVariant(variantId, qty);
      });

      updateStickyBar(cart);
    } catch (err) {
      console.error('[bundle-builder] cart sync failed:', err);
    }
  };

  /**
   * Fetch fresh cart from server and update the cache + sticky bar. Used to
   * reconcile after API calls in case of any discrepancy with our optimistic
   * predictions, and on error recovery.
   */
  const refreshStickyBarFromCart = async () => {
    try {
      const cart = await fetchCart();
      cachedCart = cart;
      updateStickyBar(cart);
    } catch (err) {
      console.error('[bundle-builder] sticky bar refresh failed:', err);
    }
  };

  const handleClaim = async (variantId) => {
    if (!variantId) return;

    // Optimistic: update local cache + UI immediately. The API call follows
    // but we DO NOT re-fetch on success - the optimistic state is built from
    // accurate inputs (variant price + product id from data attributes) and
    // our cart math is deterministic, so server will match. Re-fetching here
    // creates a race where in-flight /cart.js responses from earlier clicks
    // can overwrite newer optimistic state, causing the sticky bar to flash
    // between states on rapid clicks (per Kim's 5/27 review).
    applyLocalCartUpdate(variantId, 1);
    renderCardForVariant(variantId, 1);
    updateStickyBar(cachedCart);

    try {
      await postJson('/cart/add.js', { id: parseInt(variantId, 10), quantity: 1 });
    } catch (err) {
      console.error('[bundle-builder] add to cart failed:', err);
      // Recover from optimistic mistake by re-fetching server state
      await refreshStickyBarFromCart();
      const item = cachedCart.items?.find((i) => i.variant_id === parseInt(variantId, 10));
      renderCardForVariant(variantId, item?.quantity || 0);
    }
  };

  const handleQtyChange = async (variantId, delta) => {
    if (!variantId) return;

    const currentQty = getCurrentQty(variantId);
    const newQty = Math.max(0, currentQty + delta);

    // Optimistic: update local cache + UI immediately. See handleClaim above
    // for why we DO NOT re-fetch on success (race condition causing flashes).
    applyLocalCartUpdate(variantId, newQty);
    renderCardForVariant(variantId, newQty);
    updateStickyBar(cachedCart);
    if (newQty > 0) bumpQtyValue(variantId); // QA1 tactile feedback on +/-

    try {
      // Use /cart/update.js (not /cart/change.js) - update.js accepts a
      // variant_id-keyed updates map. Setting quantity to 0 removes the line.
      await postJson('/cart/update.js', { updates: { [variantId]: newQty } });
    } catch (err) {
      console.error('[bundle-builder] cart change failed:', err);
      // Recover from optimistic mistake by re-fetching server state
      await refreshStickyBarFromCart();
      const item = cachedCart.items?.find((i) => i.variant_id === parseInt(variantId, 10));
      renderCardForVariant(variantId, item?.quantity || 0);
    }
  };

  /**
   * Delegated click handler on the grid. Routes data-action clicks to the right
   * cart handler or opens quick view.
   */
  const wireGridActions = () => {
    const grid = document.querySelector('.ame-bundle-builder__grid');
    if (!grid) return;

    grid.addEventListener('click', (event) => {
      const actionEl = event.target.closest('[data-action]');
      if (!actionEl) return;

      const card = actionEl.closest('.ame-bundle-card[data-variant-id]');
      if (!card) return;

      event.preventDefault();
      event.stopPropagation();

      const action = actionEl.dataset.action;
      const variantId = card.dataset.variantId;

      if (action === 'claim') {
        handleClaim(variantId);
      } else if (action === 'increment') {
        handleQtyChange(variantId, 1);
      } else if (action === 'decrement') {
        handleQtyChange(variantId, -1);
      } else if (action === 'quick-view') {
        openQuickView(card);
      }
    });
  };

  /* ============================================================================
   * Quick View modal
   * ============================================================================ */

  let quickViewState = {
    variantId: null,
    images: [],
    currentIndex: 0,
    title: '',
    isAvailable: false,
  };

  const escapeHtml = (str) => {
    const div = document.createElement('div');
    div.textContent = String(str || '');
    return div.innerHTML;
  };

  const renderRatingHTML = (rating, count) => {
    // Proportional star fill (QA7) - matches snippets/ame_bundle-stars.liquid.
    // 5 grey track stars + a gold overlay clipped to (rating/5)*100%, so 4.8
    // fills the 5th star ~80% rather than flooring to 4 solid stars.
    const pct = Math.max(0, Math.min(5, rating)) * 20;
    const fiveStars = ICON_STAR_FILLED.repeat(5);
    return `
      <span class="ame-bundle-stars" aria-hidden="true">
        <span class="ame-bundle-stars__track">${fiveStars}</span>
        <span class="ame-bundle-stars__fill" style="width: ${pct}%;"><span class="ame-bundle-stars__fill-inner">${fiveStars}</span></span>
      </span>
      <span class="ame-bundle-card__rating-value">${rating.toFixed(1)}</span>
      <span class="ame-bundle-card__rating-count">(${count})</span>
    `;
  };

  /**
   * Render a money string like "$49.99" as compound spans for the modal:
   * 13.545px $/.99 + 21px integer, all brand blue.
   */
  const renderModalPriceHTML = (priceStr) => {
    if (!priceStr) return '';
    const match = String(priceStr).match(/^(\$)(\d+)(\.\d+)?$/);
    if (!match) {
      return priceStr;
    }
    const dollarSign = match[1];
    const intPart = match[2];
    const decimalPart = match[3] || '';
    return (
      `<span class="ame-bundle-quick-view__price-sub">${dollarSign}</span>` +
      `<span class="ame-bundle-quick-view__price-int">${intPart}</span>` +
      (decimalPart ? `<span class="ame-bundle-quick-view__price-sub">${decimalPart}</span>` : '')
    );
  };

  /**
   * Render the modal's action area (Claim button OR qty selector OR Out of Stock)
   * based on the current cart state for the variant the modal is showing.
   */
  const renderModalAction = () => {
    const { variantId, isAvailable, title } = quickViewState;
    const action = document.querySelector('.ame-bundle-quick-view__action');
    if (!action) return;

    if (!isAvailable) {
      action.innerHTML = `
        <button type="button" class="ame-bundle-quick-view__claim ame-bundle-quick-view__claim--disabled" disabled>
          Out of Stock
        </button>
      `;
      return;
    }

    const qty = getCurrentQty(variantId);
    if (qty > 0) {
      action.innerHTML = `
        <div class="ame-bundle-quick-view__qty">
          <button type="button" class="ame-bundle-quick-view__qty-btn" data-action="decrement" aria-label="Decrease quantity">
            ${ICON_MINUS}
          </button>
          <span class="ame-bundle-quick-view__qty-value" data-qty="${qty}">${qty}</span>
          <button type="button" class="ame-bundle-quick-view__qty-btn" data-action="increment" aria-label="Increase quantity">
            ${ICON_PLUS}
          </button>
        </div>
      `;
    } else {
      action.innerHTML = `
        <button type="button" class="ame-bundle-quick-view__claim" data-action="claim" aria-label="Claim ${escapeHtml(title)}">
          Claim ${ICON_PLUS}
        </button>
      `;
    }
  };

  /* Slide width 250px + 10px gap = 260px stride. scrollLeft of N*260 brings
     slide N into the leftmost position with the next slide peeking on the right. */
  const SLIDE_STRIDE = 260;

  /**
   * Populate the carousel track with image slides. Called once per modal open.
   */
  const populateSlides = () => {
    const track = document.querySelector('.ame-bundle-quick-view__track');
    if (!track) return;
    const { images, title } = quickViewState;
    if (!images.length) {
      track.innerHTML = '';
      return;
    }
    track.innerHTML = images
      .map(
        (src) =>
          `<div class="ame-bundle-quick-view__slide"><img class="ame-bundle-quick-view__slide-img" src="${src}" alt="${escapeHtml(title)}" loading="lazy"></div>`
      )
      .join('');
  };

  /**
   * Update active dot + nav visibility based on the current slide index.
   * Nav arrows are hidden when there's only one slide.
   */
  const renderCarouselState = () => {
    const { images, currentIndex } = quickViewState;
    const dots = document.querySelectorAll('.ame-bundle-quick-view__dot');
    dots.forEach((dot, i) => {
      dot.classList.toggle('ame-bundle-quick-view__dot--active', i === currentIndex);
      dot.setAttribute('aria-selected', i === currentIndex ? 'true' : 'false');
    });
    const navs = document.querySelectorAll('.ame-bundle-quick-view__nav');
    navs.forEach((nav) => {
      nav.style.display = images.length > 1 ? '' : 'none';
    });
  };

  /**
   * Scroll the track so slide at `index` is in the visible position.
   * Index wraps around if out of bounds.
   */
  const scrollToSlide = (index, smooth = true) => {
    const container = document.querySelector('.ame-bundle-quick-view__track-container');
    if (!container) return;
    const { images } = quickViewState;
    if (!images.length) return;
    const target = ((index % images.length) + images.length) % images.length;
    quickViewState.currentIndex = target;
    container.scrollTo({
      left: target * SLIDE_STRIDE,
      behavior: smooth ? 'smooth' : 'instant',
    });
    renderCarouselState();
  };

  /**
   * Listener for scroll events on the track container. As the user swipes or
   * scrolls the track, recompute currentIndex from scrollLeft and update the
   * active dot accordingly.
   */
  const onTrackScroll = (event) => {
    const container = event.currentTarget;
    const index = Math.round(container.scrollLeft / SLIDE_STRIDE);
    if (index !== quickViewState.currentIndex && index >= 0 && index < quickViewState.images.length) {
      quickViewState.currentIndex = index;
      renderCarouselState();
    }
  };

  const buildModalDots = () => {
    const { images } = quickViewState;
    const dotsContainer = document.querySelector('.ame-bundle-quick-view__dots');
    if (!dotsContainer) return;

    if (images.length <= 1) {
      dotsContainer.innerHTML = '';
      return;
    }

    dotsContainer.innerHTML = images
      .map(
        (_, i) =>
          `<button type="button" class="ame-bundle-quick-view__dot" data-action="goto-image" data-index="${i}" aria-label="Image ${i + 1} of ${images.length}" role="tab"></button>`
      )
      .join('');
  };

  const openQuickView = (card) => {
    if (!card) return;
    const dataScript = card.querySelector('.ame-bundle-card__quick-view-data');
    if (!dataScript) return;

    let data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (err) {
      console.error('[bundle-builder] failed to parse quick-view data', err);
      return;
    }

    quickViewState = {
      variantId: card.dataset.variantId,
      images: Array.isArray(data.images) ? data.images : [],
      currentIndex: 0,
      title: data.title || '',
      isAvailable: data.isAvailable === true,
    };

    const modal = document.getElementById(QUICK_VIEW_ID);
    if (!modal) return;

    modal.dataset.variantId = quickViewState.variantId;

    const titleEl = modal.querySelector('.ame-bundle-quick-view__product-title');
    if (titleEl) titleEl.textContent = data.title || '';

    const priceEl = modal.querySelector('.ame-bundle-quick-view__price');
    if (priceEl) priceEl.innerHTML = renderModalPriceHTML(data.price || '');

    const ratingEl = modal.querySelector('.ame-bundle-quick-view__rating');
    if (ratingEl) {
      const rating = parseFloat(data.rating) || 0;
      const count = parseInt(data.ratingCount, 10) || 0;
      ratingEl.innerHTML = rating > 0 ? renderRatingHTML(rating, count) : '';
    }

    buildModalDots();
    populateSlides();
    renderModalAction();

    if (typeof modal.showModal === 'function') {
      modal.showModal();
    } else {
      modal.setAttribute('open', '');
    }

    // Reset the carousel to the first slide AFTER the dialog is shown (William
    // 6/1). Doing it before showModal() is a no-op: while the dialog is closed
    // it's display:none, so the track has no layout and scrollTo() doesn't take
    // - which left the previous scroll position (e.g. image 3) on reopen while
    // the dots reset to image 1. requestAnimationFrame waits for the dialog's
    // layout, then we force scroll to 0 and re-sync the dots.
    quickViewState.currentIndex = 0;
    requestAnimationFrame(() => {
      scrollToSlide(0, false);
    });

    // QA9: lock background scroll while the modal is open so the page behind
    // can't scroll away from the modal. Released on the dialog 'close' event
    // (wired in wireModalActions) which covers Esc, backdrop, and button close.
    document.body.classList.add('ame-bundle-scroll-locked');
  };

  const closeQuickView = () => {
    const modal = document.getElementById(QUICK_VIEW_ID);
    if (!modal) return;
    if (typeof modal.close !== 'function') {
      modal.removeAttribute('open');
      return;
    }
    // QA1: play the closing animation before actually closing. The [open] rule
    // only animates opening, so we add --closing (while still open) to run the
    // reverse, then call close() on animationend. The 'close' handler strips the
    // class so the next open animates in cleanly. Timeout is a safety fallback.
    if (modal.classList.contains('ame-bundle-quick-view--closing')) return;
    modal.classList.add('ame-bundle-quick-view--closing');
    let closed = false;
    const finish = () => {
      if (closed) return;
      closed = true;
      modal.close();
    };
    modal.addEventListener('animationend', finish, { once: true });
    setTimeout(finish, 240);
  };

  const goToImage = (index) => scrollToSlide(index);
  const nextImage = () => scrollToSlide(quickViewState.currentIndex + 1);
  const prevImage = () => scrollToSlide(quickViewState.currentIndex - 1);

  /**
   * Delegated click handler on the modal. Routes data-action clicks for cart
   * actions, carousel nav, and close.
   */
  const wireModalActions = () => {
    const modal = document.getElementById(QUICK_VIEW_ID);
    if (!modal) return;

    // QA9: release the background scroll lock whenever the dialog closes - the
    // native 'close' event fires for every close path (Esc key, backdrop click,
    // and the close button), so this is the single reliable place to unlock.
    modal.addEventListener('close', () => {
      document.body.classList.remove('ame-bundle-scroll-locked');
      // Strip the closing class so the next open plays the open animation.
      modal.classList.remove('ame-bundle-quick-view--closing');
    });

    // Wire track scroll listener for swipe-based navigation (updates active dot)
    const trackContainer = modal.querySelector('.ame-bundle-quick-view__track-container');
    if (trackContainer) {
      trackContainer.addEventListener('scroll', onTrackScroll, { passive: true });
    }

    modal.addEventListener('click', (event) => {
      // Backdrop click closes the modal (event target is the dialog itself)
      if (event.target === modal) {
        closeQuickView();
        return;
      }

      const actionEl = event.target.closest('[data-action]');
      if (!actionEl) return;

      event.preventDefault();
      event.stopPropagation();

      const action = actionEl.dataset.action;
      const variantId = modal.dataset.variantId;

      if (action === 'claim') {
        handleClaim(variantId);
      } else if (action === 'increment') {
        handleQtyChange(variantId, 1);
      } else if (action === 'decrement') {
        handleQtyChange(variantId, -1);
      } else if (action === 'close-quick-view') {
        closeQuickView();
      } else if (action === 'prev-image') {
        prevImage();
      } else if (action === 'next-image') {
        nextImage();
      } else if (action === 'goto-image') {
        const idx = parseInt(actionEl.dataset.index, 10);
        if (!Number.isNaN(idx)) {
          goToImage(idx);
        }
      }
    });
  };

  /* ============================================================================
   * Init
   * ============================================================================ */

  const init = () => {
    const initial = getCategoryFromUrl();
    sortGridByRevenue();
    updateCategoryCardSelection(initial);
    updateProductVisibility(initial);
    wireCategoryClicks();
    wireGridActions();
    wireModalActions();
    collectNecklaceProductIds();
    syncCartState();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
